import { Approver, DealContractFields, FieldUpdate, OrgTableUpdateItem, Requestor } from 'types'
import isEqual from 'lodash.isequal'

export const requestApprovalUpdates = (
  tableName: string,
  PK: string,
  SK: string,
  requestor: Requestor,
  activeUserId: string,
  approvers: Approver[]
): OrgTableUpdateItem[] => [
  {
    tableName: tableName,
    PK: PK,
    SK: SK,
    fieldName: 'isRequested',
    newValue: true,
  },
  {
    tableName: tableName,
    PK: PK,
    SK: SK,
    fieldName: 'requestedBy',
    newValue: activeUserId,
  },
  {
    tableName: tableName,
    PK: PK,
    SK: SK,
    fieldName: 'approvers',
    newValue: (approvers as unknown) as Record<string, unknown>[],
  },
  {
    tableName: tableName,
    PK: PK,
    SK: SK,
    fieldName: 'status',
    newValue: 'Approval Requested',
  },
  {
    tableName: tableName,
    PK: PK,
    SK: SK,
    fieldName: 'stage',
    newValue: 'Pending Approval',
  },
  {
    tableName: tableName,
    PK: PK,
    SK: SK,
    fieldName: 'requestedTime',
    newValue: new Date().toISOString(),
  },
  {
    tableName: tableName,
    PK: PK,
    SK: SK,
    fieldName: 'requestor',
    newValue: requestor
      ? {
          id: requestor.id,
          name: requestor.name,
          email: requestor.email,
          image: requestor.image,
          hasRequested: true,
        }
      : {},
  },
]
export const getDealFieldUpdates = (
  original: Partial<DealContractFields>,
  current: Partial<DealContractFields>
): FieldUpdate[] => {
  const updatedFields: { tableName: string; PK: string; SK: string; fieldName: string; newValue: any }[] = []

  const fields: (keyof DealContractFields)[] = [
    'recipients',
    'type',
    'numberOfPayments',
    'startDate',
    'endDate',
    'dependenciesDueDate',
    'targetLaunchDate',
    'noSignature',
    'contractId',
    'status',
    'stage',
    'isDraft',
    'isReverted',
    'linksGenerated',
    'lastModified',
  ]

  for (const fieldName of fields) {
    const orig = original[fieldName]
    const curr = current[fieldName]

    const hasChanged = typeof orig === 'object' ? JSON.stringify(orig) !== JSON.stringify(curr) : orig !== curr

    if (hasChanged) {
      updatedFields.push({
        tableName: 'ORGANIZATIONS_TABLE',
        PK: current?.PK,
        SK: current?.SK,
        fieldName,
        newValue: curr,
      })
    }
  }

  return updatedFields
}

export const detectDealChanges = (
  original: Partial<DealContractFields>,
  current: Partial<DealContractFields>
): boolean => {
  const fields: (keyof DealContractFields)[] = [
    'recipients',
    'type',
    'numberOfPayments',
    'startDate',
    'endDate',
    'dependenciesDueDate',
    'targetLaunchDate',
    'noSignature',
    'contractId',
    'status',
    'stage',
    'isDraft',
    'isReverted',
    'linksGenerated',
    'lastModified',
  ]

  return fields.some(field => !isEqual(original[field], current[field]))
}
export const getDealBatchReplaceItems = (
  original: Partial<DealContractFields>,
  current: Partial<DealContractFields>
): {
  tableName: string
  PK: string
  SK: string
  newItem: DealContractFields
}[] => {
  // ─────────────────────────────────────────────────────────────────────────────
  // VALIDATION: Prevent corrupted/incomplete deals from being sent to backend
  // ─────────────────────────────────────────────────────────────────────────────
  if (!current || typeof current !== 'object') {
    console.error('[getDealBatchReplaceItems] VALIDATION FAILED: current is not an object', { current })
    return []
  }

  if (!current.PK || !current.SK) {
    console.error('[getDealBatchReplaceItems] VALIDATION FAILED: missing PK or SK', {
      PK: current.PK,
      SK: current.SK,
    })
    return []
  }

  // For deals, require critical fields to prevent data loss
  if (current.SK?.startsWith('DEAL_')) {
    // Check for 'type' which is on DealContractFields interface
    if (!current.type) {
      console.error('[getDealBatchReplaceItems] VALIDATION FAILED: deal missing required type field', {
        PK: current.PK,
        SK: current.SK,
        receivedFields: Object.keys(current),
      })
      return []
    }
  }

  // Ensure deal has more than just PK/SK to prevent wiping data
  const nonKeyFields = Object.keys(current).filter(k => k !== 'PK' && k !== 'SK')
  if (nonKeyFields.length < 3) {
    console.error('[getDealBatchReplaceItems] VALIDATION FAILED: deal appears incomplete', {
      PK: current.PK,
      SK: current.SK,
      fieldCount: nonKeyFields.length,
      fields: nonKeyFields,
    })
    return []
  }

  const fieldsToCheck: (keyof DealContractFields)[] = [
    'recipients',
    'type',
    'numberOfPayments',
    'startDate',
    'endDate',
    'dependenciesDueDate',
    'targetLaunchDate',
    'noSignature',
    'contractId',
    'status',
    'stage',
    'isDraft',
    'isReverted',
    'linksGenerated',
    'lastModified',
    'totals',
  ]

  const hasChanges = fieldsToCheck.some(field => !isEqual(original[field], current[field]))

  if (!hasChanges) return []

  return [
    {
      tableName: 'ORGANIZATIONS_TABLE',
      PK: current.PK!,
      SK: current.SK!,
      newItem: {
        ...current,
        PK: current.PK!,
        SK: current.SK!,
      } as DealContractFields,
    },
  ]
}
export const getApprovalBatchReplaceItem = (
  tableName: string,
  PK: string,
  SK: string,
  requestor: Requestor,
  activeUserId: string,
  approvers: Approver[],
  currentDeal: DealContractFields
) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // VALIDATION: Prevent corrupted deals from being sent to backend
  // ─────────────────────────────────────────────────────────────────────────────
  if (!currentDeal || typeof currentDeal !== 'object') {
    console.error('[getApprovalBatchReplaceItem] VALIDATION FAILED: currentDeal is not an object', { currentDeal })
    return []
  }

  if (!PK || !SK) {
    console.error('[getApprovalBatchReplaceItem] VALIDATION FAILED: missing PK or SK', { PK, SK })
    return []
  }

  // Require critical fields to prevent data loss
  if (!currentDeal.type) {
    console.error('[getApprovalBatchReplaceItem] VALIDATION FAILED: deal missing required type field', {
      PK,
      SK,
      hasType: !!currentDeal.type,
      receivedFields: Object.keys(currentDeal),
    })
    return []
  }

  const newItem: DealContractFields = {
    ...currentDeal,
    PK,
    SK,
    isRequested: true,
    requestedBy: activeUserId,
    approvers: approvers,
    status: 'Approval Requested',
    stage: 'Pending Approval',
    requestedTime: new Date().toISOString(),
    requestor: requestor
      ? {
          id: requestor.id,
          name: requestor.name,
          email: requestor.email,
          image: requestor.image_512,
          hasRequested: true,
        }
      : ({} as Requestor),
  }

  return [
    {
      tableName,
      PK,
      SK,
      newItem,
    },
  ]
}
